// Ui bus — selection (info pane subject), view jump requests, guidance
// helpers (select / view_requested).

import * as vsj from './vsj';

type Handler = (arg: string) => void;

const selectHandlers: Handler[] = [];
const viewHandlers: Handler[] = [];

export let selected = '';
export let selectionCtx: { inv: string; slot: string | number } | null = null;

/** Select an info subject: an infos id, or "tile:x,y". Slots pass their
    {inv, slot} context so the item sheet gets an action row + live durability. */
export function select(id: string, ctx: { inv: string; slot: string | number } | null = null): void {
  selected = id;
  selectionCtx = ctx;
  for (const fn of selectHandlers) fn(id);
}

export function onSelect(fn: Handler): void {
  selectHandlers.push(fn);
}

/** Ask the world chrome to switch to a view tab (user-driven jumps only —
    guidance never auto-selects). */
export function requestView(tab: string): void {
  for (const fn of viewHandlers) fn(tab);
}

export function onViewRequest(fn: Handler): void {
  viewHandlers.push(fn);
}

/** Guidance glow test. Keys per CODEMAP "Guidance glow keys". */
export function glows(key: string): boolean {
  const g = vsj.get().guidance?.glow;
  return Array.isArray(g) && g.includes(key);
}

/** Reduced-motion: the Settings toggle (html[data-motion]) or the OS pref.
    JS-driven juice (sparks, flights, map living motion) checks this; CSS
    handles its own via the attribute/media query. */
export function reducedMotion(): boolean {
  return (
    document.documentElement.dataset.motion === 'reduced' ||
    (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches)
  );
}

// ---- "take me there": a user-driven jump lands scrolled-to and flashing ----
let pendingFlash: { view: string; key: string } | null = null;

/** Jump to a view AND point at a card in it: the view scrolls the card into
    sight and flashes it (stoneheim gotoAct scroll-and-flash pattern). */
export function takeThere(view: string, key: string): void {
  pendingFlash = { view, key };
  requestView(view);
}

/** Views call this from onShown; returns (and clears) the card key to flash. */
export function consumeFlash(view: string): string | null {
  if (pendingFlash?.view !== view) return null;
  const k = pendingFlash.key;
  pendingFlash = null;
  return k;
}

/** Scroll a jumped-to card into the middle of its scroller and flash it. */
export function flashTarget(node: HTMLElement | null): void {
  if (!node) return;
  node.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
  node.classList.remove('take-flash');
  void node.offsetWidth;
  node.classList.add('take-flash');
  setTimeout(() => node.classList.remove('take-flash'), 1300);
}
